import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Download, Monitor, Smartphone, Globe, Apple, 
  CheckCircle2, ArrowLeft, Loader2, Share2
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

// Windows icon component
const WindowsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
  </svg>
);

const DownloadsPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [platform, setPlatform] = useState('unknown');

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) setPlatform('windows');
    else if (userAgent.includes('mac')) setPlatform('mac');
    else if (userAgent.includes('linux')) setPlatform('linux');
    else if (userAgent.includes('android')) setPlatform('android');
    else if (userAgent.includes('iphone') || userAgent.includes('ipad')) setPlatform('ios');

    // Check if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((reg) => console.log('SW registered:', reg.scope))
        .catch((err) => console.log('SW registration failed:', err));
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) {
      // Show manual install instructions
      alert('To install: Click the browser menu (⋮) and select "Install App" or "Add to Home Screen"');
      return;
    }

    setIsInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
    setIsInstalling(false);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'SlideCon',
          text: 'AI-Powered Slideshow Generator - Transform documents into beautiful presentations',
          url: window.location.origin
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  const downloads = [
    {
      id: 'pwa',
      name: 'Web App',
      description: 'Install directly from your browser. Works on any device.',
      icon: <Globe className="w-8 h-8" />,
      badge: 'Recommended',
      badgeColor: 'bg-green-500',
      size: '~2 MB',
      features: ['Works offline', 'Auto-updates', 'No download required'],
      action: handleInstallPWA,
      actionText: isInstalled ? 'Installed' : (deferredPrompt ? 'Install Now' : 'Install App'),
      disabled: isInstalled,
      loading: isInstalling
    },
    {
      id: 'windows',
      name: 'Windows',
      description: 'Native desktop app for Windows 10/11',
      icon: <WindowsIcon className="w-8 h-8" />,
      badge: 'Desktop',
      badgeColor: 'bg-blue-500',
      size: '~85 MB',
      features: ['Native performance', 'System integration', 'Offline support'],
      downloadUrl: '/downloads/SlideCon-Setup.exe',
      actionText: 'Download .exe',
      highlight: platform === 'windows'
    },
    {
      id: 'mac',
      name: 'macOS',
      description: 'Native desktop app for macOS 11+',
      icon: <Apple className="w-8 h-8" />,
      badge: 'Desktop',
      badgeColor: 'bg-slate-500',
      size: '~90 MB',
      features: ['Native performance', 'M1/M2 optimized', 'Offline support'],
      downloadUrl: '/downloads/SlideCon.dmg',
      actionText: 'Download .dmg',
      highlight: platform === 'mac'
    },
    {
      id: 'mobile',
      name: 'Mobile App',
      description: 'Add to your home screen for app-like experience',
      icon: <Smartphone className="w-8 h-8" />,
      badge: 'Mobile',
      badgeColor: 'bg-purple-500',
      size: '~2 MB',
      features: ['Full-screen mode', 'Home screen icon', 'Push notifications'],
      action: handleInstallPWA,
      actionText: 'Add to Home Screen',
      show: platform === 'android' || platform === 'ios'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-xl">{t('download')} SlideCon</h1>
              <p className="text-sm text-slate-400">Choose your platform</p>
            </div>
          </div>
          {navigator.share && (
            <Button variant="outline" size="sm" onClick={handleShare}>
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Download className="w-10 h-10 text-black" />
          </div>
          <h2 className="text-3xl font-bold mb-3">Get SlideCon on Any Device</h2>
          <p className="text-slate-400 max-w-lg mx-auto">
            Download our desktop apps for the best experience, or install the web app for instant access from any browser.
          </p>
        </motion.div>

        {/* Download Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {downloads.filter(d => d.show !== false).map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className={`bg-slate-900 border-slate-800 h-full ${item.highlight ? 'ring-2 ring-amber-500' : ''}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      item.id === 'pwa' ? 'bg-green-500/20 text-green-400' :
                      item.id === 'windows' ? 'bg-blue-500/20 text-blue-400' :
                      item.id === 'mac' ? 'bg-slate-500/20 text-slate-300' :
                      'bg-purple-500/20 text-purple-400'
                    }`}>
                      {item.icon}
                    </div>
                    <Badge className={`${item.badgeColor} text-white`}>
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-white mt-4">{item.name}</CardTitle>
                  <CardDescription className="text-slate-400">{item.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {item.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                    <span className="text-xs text-slate-500">{item.size}</span>
                    
                    {item.downloadUrl ? (
                      <Button 
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                        asChild
                      >
                        <a href={item.downloadUrl} download>
                          <Download className="w-4 h-4 mr-2" />
                          {item.actionText}
                        </a>
                      </Button>
                    ) : (
                      <Button 
                        onClick={item.action}
                        disabled={item.disabled || item.loading}
                        className={item.disabled ? 'bg-green-600' : 'bg-amber-500 hover:bg-amber-600 text-black'}
                      >
                        {item.loading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : item.disabled ? (
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        {item.actionText}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* System Requirements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <h3 className="text-xl font-semibold mb-6 text-center">System Requirements</h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm">
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-5 h-5 text-green-400" />
                <span className="font-medium">Web App (PWA)</span>
              </div>
              <ul className="space-y-1 text-slate-400">
                <li>• Chrome, Edge, Firefox, or Safari</li>
                <li>• Any modern device with a browser</li>
                <li>• Internet connection for first load</li>
              </ul>
            </div>
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <WindowsIcon className="w-5 h-5 text-blue-400" />
                <span className="font-medium">Windows</span>
              </div>
              <ul className="space-y-1 text-slate-400">
                <li>• Windows 10 or later (64-bit)</li>
                <li>• 4 GB RAM minimum</li>
                <li>• 200 MB disk space</li>
              </ul>
            </div>
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <Apple className="w-5 h-5 text-slate-300" />
                <span className="font-medium">macOS</span>
              </div>
              <ul className="space-y-1 text-slate-400">
                <li>• macOS 11 (Big Sur) or later</li>
                <li>• Intel or Apple Silicon</li>
                <li>• 200 MB disk space</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default DownloadsPage;
